import {useCallback, useEffect, useRef, useState} from 'react';

import {inviteService} from '@/services/merchants/inviteService';
import type {
  AfriexQueryParams,
  BusinessAdminInviteRequest,
  BusinessOnboarding,
  BusinessOnboardingEditRequest,
} from '@/types';
import {getResponseMessage} from '@/utils/errorMessage';
import {logRejection} from '@/utils/logRejection';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';

import type {InviteAction} from './InvitesTable';

const PAGE_SIZE = 10;

const useBusinessInvites = () => {
  const [invites, setInvites] = useState<BusinessOnboarding[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isPageReset, setIsPageReset] = useState(false);
  // Track the last-fetched page so we can stay on it after an action
  const currentPageRef = useRef<number>(0); // 0-indexed, matching API
  // 1-indexed display page, synced back to Pagination after a direct fetch
  const [displayPage, setDisplayPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const [selectedInvite, setSelectedInvite] =
    useState<BusinessOnboarding | null>(null);
  const [pendingAction, setPendingAction] = useState<InviteAction | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);

  // Depends on searchTerm so its reference changes when the search does,
  // triggering the useEffect below.
  const fetchInvites = useCallback(
    async (params?: AfriexQueryParams) => {
      if (params?.page !== undefined) {
        currentPageRef.current = params.page;
        setDisplayPage(params.page + 1);
      }
      try {
        setIsLoading(true);
        const data = await inviteService.getOnboardingInvites({
          ...params,
          ...(searchTerm && {searchTerm}),
        });
        setInvites(data.list);
        setTotal(data.total);
      } catch (error) {
        showErrorMessage(getResponseMessage(error) || 'Failed to load invites');
      } finally {
        setIsLoading(false);
      }
    },
    [searchTerm],
  );

  useEffect(() => {
    // `fetchInvites` toasts its own failures, so this only fires if that catch
    // or the finally itself throws. Log rather than swallow silently.
    fetchInvites({page: 0, limit: PAGE_SIZE}).catch(
      logRejection('Failed to load business invites'),
    );
  }, [fetchInvites]);

  const handleSearch = useCallback((search: string) => {
    setSearchTerm(search.trim());
    setIsPageReset((prev) => !prev);
  }, []);

  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setIsPageReset((prev) => !prev);
  }, []);

  const openConfirm = (invite: BusinessOnboarding, action: InviteAction) => {
    setSelectedInvite(invite);
    setPendingAction(action);
    setIsConfirmOpen(true);
  };

  const closeConfirm = () => {
    setIsConfirmOpen(false);
    setSelectedInvite(null);
    setPendingAction(null);
  };

  const handleConfirmAction = async () => {
    if (!selectedInvite?.id || !pendingAction) {
      return;
    }
    try {
      setIsActionLoading(true);
      if (pendingAction === 'deny') {
        await inviteService.denyOnboardingInvite(selectedInvite.id);
        showSuccessMessage('Invite denied');
      } else {
        await inviteService.sendOnboardingInviteLink(selectedInvite.id);
        showSuccessMessage(
          pendingAction === 'approve' ? 'Invite approved' : 'Invite link sent',
        );
      }
      closeConfirm();
      await fetchInvites({page: currentPageRef.current, limit: PAGE_SIZE});
    } catch (error) {
      showErrorMessage(
        getResponseMessage(error) || 'Action failed. Please try again.',
      );
      closeConfirm();
      // A refusal usually means the invite changed underneath the admin (its
      // business now exists, or a duplicate was approved), so show the list as
      // it is now.
      await fetchInvites({page: currentPageRef.current, limit: PAGE_SIZE});
    } finally {
      setIsActionLoading(false);
    }
  };

  const openEditModal = (invite: BusinessOnboarding) => {
    setSelectedInvite(invite);
    setIsEditOpen(true);
  };
  const closeEditModal = () => {
    setIsEditOpen(false);
    setSelectedInvite(null);
  };

  const handleEditInvite = async (payload: BusinessOnboardingEditRequest) => {
    if (!selectedInvite?.id) {
      return;
    }
    try {
      setIsActionLoading(true);
      await inviteService.updateOnboardingInvite(selectedInvite.id, payload);
      showSuccessMessage('Invite updated');
      closeEditModal();
      await fetchInvites({page: currentPageRef.current, limit: PAGE_SIZE});
    } catch (error) {
      showErrorMessage(
        getResponseMessage(error) ||
          'Failed to update invite. Please try again.',
      );
    } finally {
      setIsActionLoading(false);
    }
  };

  const openCreateModal = () => setIsCreateOpen(true);
  const closeCreateModal = () => setIsCreateOpen(false);

  const handleCreateInvite = async (payload: BusinessAdminInviteRequest) => {
    try {
      setIsActionLoading(true);
      await inviteService.createOnboardingInvite(payload);
      showSuccessMessage('Invite created successfully');
      closeCreateModal();
      setIsPageReset((prev) => !prev);
      await fetchInvites({page: 0, limit: PAGE_SIZE});
    } catch (error) {
      showErrorMessage(
        getResponseMessage(error) ||
          'Failed to create invite. Please try again.',
      );
    } finally {
      setIsActionLoading(false);
    }
  };

  return {
    invites,
    total,
    isLoading,
    isActionLoading,
    isPageReset,
    displayPage,
    selectedInvite,
    pendingAction,
    isConfirmOpen,
    isCreateOpen,
    isEditOpen,
    isSearchActive: searchTerm.length > 0,
    handleSearch,
    fetchInvites,
    openConfirm,
    closeConfirm,
    handleConfirmAction,
    openEditModal,
    closeEditModal,
    handleEditInvite,
    openCreateModal,
    closeCreateModal,
    handleCreateInvite,
    clearFilters,
  };
};

export default useBusinessInvites;
